import React, { useState, useEffect } from 'react';
import {
  X,
  Play,
  Square,
  Pause,
  Clock,
  CheckCircle2,
  Circle,
  Plus,
  Trash2,
  Share2,
  Star,
  Tag,
  Calendar,
  AlertCircle,
  MessageSquare,
  Send,
  GitBranch,
  Link2,
  User,
  History,
  CheckSquare,
} from 'lucide-react';
import api from '../../api/client';

export default function TaskDetailDrawer({
  taskId,
  isOpen,
  onClose,
  onTaskUpdated,
  allProjectTasks = [],
}) {
  const [task, setTask] = useState(null);
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('overview'); // overview, subtasks, timer, dependencies, comments, activity
  const [newSubtaskTitle, setNewSubtaskTitle] = useState('');
  const [addingSubtask, setAddingSubtask] = useState(false);
  const [timerNotes, setTimerNotes] = useState('');
  const [timerRunning, setTimerRunning] = useState(false);
  const [elapsedSeconds, setElapsedSeconds] = useState(0);
  const [commentText, setCommentText] = useState('');
  const [addingComment, setAddingComment] = useState(false);
  const [selectedDepTaskId, setSelectedDepTaskId] = useState('');
  const [depType, setDepType] = useState('FINISH_TO_START');
  const [depError, setDepError] = useState('');

  const fetchTaskDetail = async () => {
    if (!taskId) return;
    setLoading(true);
    try {
      const res = await api.get(`/tasks/${taskId}`);
      setTask(res.data);
    } catch (err) {
      console.error('Failed to fetch task details:', err);
    } finally {
      setLoading(false);
    }
  };

  // Check active timer on mount or open
  useEffect(() => {
    if (isOpen && taskId) {
      fetchTaskDetail();
      checkActiveTimer();
    }
  }, [isOpen, taskId]);

  // Lock body scroll on mobile when drawer is open
  useEffect(() => {
    if (isOpen) document.body.classList.add('mobile-modal-open');
    else document.body.classList.remove('mobile-modal-open');
    return () => document.body.classList.remove('mobile-modal-open');
  }, [isOpen]);

  const checkActiveTimer = async () => {
    try {
      const res = await api.get('/tasks/timer/active');
      if (res.data && res.data.task_id === taskId) {
        setTimerRunning(true);
        setElapsedSeconds(res.data.elapsedSeconds || 0);
      } else {
        setTimerRunning(false);
      }
    } catch (err) {
      // ignore
    }
  };

  // Live timer interval
  useEffect(() => {
    let interval = null;
    if (timerRunning) {
      interval = setInterval(() => {
        setElapsedSeconds((prev) => prev + 1);
      }, 1000);
    }
    return () => {
      if (interval) clearInterval(interval);
    };
  }, [timerRunning]);

  if (!isOpen) return null;

  const formatSeconds = (sec) => {
    const h = Math.floor(sec / 3600);
    const m = Math.floor((sec % 3600) / 60);
    const s = sec % 60;
    return `${h.toString().padStart(2, '0')}:${m.toString().padStart(2, '0')}:${s.toString().padStart(2, '0')}`;
  };

  const handleStartTimer = async () => {
    try {
      await api.post(`/tasks/${taskId}/timer/start`, { notes: timerNotes });
      setTimerRunning(true);
      setElapsedSeconds(0);
      fetchTaskDetail();
    } catch (err) {
      alert(err.response?.data?.message || 'Failed to start timer');
    }
  };

  const handleStopTimer = async () => {
    try {
      await api.post(`/tasks/${taskId}/timer/stop`, { notes: timerNotes });
      setTimerRunning(false);
      setTimerNotes('');
      fetchTaskDetail();
      if (onTaskUpdated) onTaskUpdated();
    } catch (err) {
      alert(err.response?.data?.message || 'Failed to stop timer');
    }
  };

  const handleToggleFavorite = async () => {
    try {
      const res = await api.post(`/tasks/${taskId}/favorite`);
      setTask((prev) => ({ ...prev, is_favorite: res.data.is_favorite }));
      if (onTaskUpdated) onTaskUpdated();
    } catch (err) {
      console.error(err);
    }
  };

  const handleStatusChange = async (newStatus) => {
    try {
      const res = await api.put(`/tasks/${taskId}`, { status: newStatus });
      setTask(res.data);
      if (onTaskUpdated) onTaskUpdated();
    } catch (err) {
      alert(err.response?.data?.message || 'Failed to update status');
    }
  };

  const handleToggleSubtask = async (subtask) => {
    const nextStatus = subtask.status === 'COMPLETED' ? 'TODO' : 'COMPLETED';
    try {
      await api.put(`/tasks/${subtask.id}`, { status: nextStatus });
      fetchTaskDetail();
      if (onTaskUpdated) onTaskUpdated();
    } catch (err) {
      alert(err.response?.data?.message || 'Failed to toggle subtask');
    }
  };

  const handleAddSubtask = async (e) => {
    e.preventDefault();
    if (!newSubtaskTitle.trim()) return;
    setAddingSubtask(true);
    try {
      await api.post('/tasks', {
        parent_task_id: taskId,
        project_id: task?.project_id,
        title: newSubtaskTitle.trim(),
        status: 'TODO',
      });
      setNewSubtaskTitle('');
      fetchTaskDetail();
      if (onTaskUpdated) onTaskUpdated();
    } catch (err) {
      alert(err.response?.data?.message || 'Failed to add subtask');
    } finally {
      setAddingSubtask(false);
    }
  };

  const handleAddComment = async (e) => {
    e.preventDefault();
    if (!commentText.trim()) return;
    setAddingComment(true);
    try {
      // Find any @mentions if present (e.g. @user)
      const mentionMatches = commentText.match(/@(\w+)/g) || [];
      await api.post(`/tasks/${taskId}/comments`, {
        content: commentText.trim(),
        mentions: [],
      });
      setCommentText('');
      fetchTaskDetail();
    } catch (err) {
      alert(err.response?.data?.message || 'Failed to post comment');
    } finally {
      setAddingComment(false);
    }
  };

  const handleAddDependency = async (e) => {
    e.preventDefault();
    if (!selectedDepTaskId) return;
    setDepError('');
    try {
      await api.post(`/tasks/${taskId}/dependencies`, {
        depends_on_task_id: selectedDepTaskId,
        dependency_type: depType,
      });
      setSelectedDepTaskId('');
      fetchTaskDetail();
      if (onTaskUpdated) onTaskUpdated();
    } catch (err) {
      setDepError(err.response?.data?.message || 'Failed to link dependency');
    }
  };

  const handleRemoveDependency = async (depId) => {
    try {
      await api.delete(`/tasks/${taskId}/dependencies/${depId}`);
      fetchTaskDetail();
      if (onTaskUpdated) onTaskUpdated();
    } catch (err) {
      console.error(err);
    }
  };

  const handleDeleteTask = async () => {
    if (!window.confirm('Move this task to Trash?')) return;
    try {
      await api.delete(`/tasks/${taskId}`);
      onClose();
      if (onTaskUpdated) onTaskUpdated();
    } catch (err) {
      alert(err.response?.data?.message || 'Failed to delete task');
    }
  };

  const subtasks = task?.subtasks || [];
  const completedSubs = subtasks.filter((s) => s.status === 'COMPLETED').length;
  const progressPercent = subtasks.length > 0
    ? Math.round((completedSubs / subtasks.length) * 100)
    : (task?.status === 'COMPLETED' ? 100 : task?.progress || 0);

  return (
    <div
      style={{
        position: 'fixed',
        top: 0,
        right: 0,
        bottom: 0,
        width: '560px',
        maxWidth: '100vw',
        background: 'var(--bg-secondary)',
        borderLeft: '1px solid var(--border-color)',
        zIndex: 1050,
        display: 'flex',
        flexDirection: 'column',
        boxShadow: '-8px 0 32px rgba(0,0,0,0.4)',
        animation: 'slideInRight 0.25s ease-out',
      }}
    >
      {/* Top Header */}
      <div
        style={{
          padding: '16px 20px',
          borderBottom: '1px solid var(--border-color)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          background: 'var(--bg-surface)',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <button
            onClick={handleToggleFavorite}
            style={{
              background: 'transparent',
              border: 'none',
              cursor: 'pointer',
              color: task?.is_favorite ? '#eab308' : 'var(--text-secondary)',
              padding: '4px',
            }}
            title="Toggle Favorite"
          >
            <Star size={18} fill={task?.is_favorite ? '#eab308' : 'none'} />
          </button>
          <span style={{ fontSize: '13px', color: 'var(--text-secondary)' }}>
            {task?.project_name ? `${task.project_name} / ` : 'Personal / '}Task
          </span>
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <button
            onClick={handleDeleteTask}
            style={{
              background: 'transparent',
              border: 'none',
              color: 'var(--text-secondary)',
              cursor: 'pointer',
              padding: '6px',
            }}
            title="Delete Task"
          >
            <Trash2 size={16} />
          </button>
          <button
            onClick={onClose}
            style={{
              background: 'transparent',
              border: 'none',
              color: 'var(--text-secondary)',
              cursor: 'pointer',
              padding: '6px',
            }}
          >
            <X size={20} />
          </button>
        </div>
      </div>

      {loading && !task ? (
        <div style={{ padding: '32px', textAlign: 'center', color: 'var(--text-secondary)' }}>
          Loading task details...
        </div>
      ) : (
        <>
          {/* Main Title & Status Bar */}
          <div style={{ padding: '20px', borderBottom: '1px solid var(--border-color)' }}>
            <h2 style={{ margin: '0 0 12px 0', fontSize: '19px', fontWeight: 600, color: 'var(--text-primary)' }}>
              {task?.title}
            </h2>

            {/* Quick status & priority toggles */}
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px', alignItems: 'center' }}>
              <select
                className="input-field"
                value={task?.status || 'TODO'}
                onChange={(e) => handleStatusChange(e.target.value)}
                style={{ padding: '4px 10px', fontSize: '12px', fontWeight: 600, borderRadius: '6px' }}
              >
                <option value="BACKLOG">Backlog</option>
                <option value="TODO">To Do</option>
                <option value="IN_PROGRESS">In Progress</option>
                <option value="REVIEW">In Review</option>
                <option value="BLOCKED">Blocked</option>
                <option value="COMPLETED">Completed</option>
              </select>

              <span
                style={{
                  padding: '3px 8px',
                  borderRadius: '6px',
                  fontSize: '11px',
                  fontWeight: 600,
                  textTransform: 'uppercase',
                  background:
                    task?.priority === 'URGENT'
                      ? 'rgba(239, 68, 68, 0.2)'
                      : task?.priority === 'HIGH'
                      ? 'rgba(245, 158, 11, 0.2)'
                      : 'rgba(59, 130, 246, 0.15)',
                  color:
                    task?.priority === 'URGENT'
                      ? 'var(--danger)'
                      : task?.priority === 'HIGH'
                      ? 'var(--warning)'
                      : 'var(--accent-primary)',
                }}
              >
                {task?.priority}
              </span>

              {task?.category_name && (
                <span
                  style={{
                    padding: '3px 8px',
                    borderRadius: '6px',
                    fontSize: '11px',
                    background: 'rgba(255,255,255,0.08)',
                    color: task.category_color || 'var(--text-secondary)',
                  }}
                >
                  📁 {task.category_name}
                </span>
              )}
            </div>

            {/* Progress bar */}
            <div style={{ marginTop: '16px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '12px', color: 'var(--text-secondary)', marginBottom: '4px' }}>
                <span>Subtasks Progress ({completedSubs}/{subtasks.length})</span>
                <span>{progressPercent}%</span>
              </div>
              <div style={{ height: '6px', width: '100%', background: 'rgba(255,255,255,0.08)', borderRadius: '3px', overflow: 'hidden' }}>
                <div
                  style={{
                    height: '100%',
                    width: `${progressPercent}%`,
                    background: progressPercent === 100 ? 'var(--success)' : 'var(--accent-primary)',
                    transition: 'width 0.3s ease',
                  }}
                />
              </div>
            </div>
          </div>

          {/* Navigation Tabs */}
          <div
            style={{
              display: 'flex',
              borderBottom: '1px solid var(--border-color)',
              background: 'var(--bg-surface)',
              overflowX: 'auto',
            }}
          >
            {[
              { id: 'overview', label: 'Overview' },
              { id: 'subtasks', label: `Subtasks (${subtasks.length})` },
              { id: 'timer', label: 'Time Tracking' },
              { id: 'dependencies', label: `Deps (${task?.dependencies?.length || 0})` },
              { id: 'comments', label: `Comments (${task?.comments?.length || 0})` },
            ].map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                style={{
                  padding: '10px 14px',
                  background: 'transparent',
                  border: 'none',
                  borderBottom: activeTab === tab.id ? '2px solid var(--accent-primary)' : '2px solid transparent',
                  color: activeTab === tab.id ? 'var(--text-primary)' : 'var(--text-secondary)',
                  fontSize: '13px',
                  fontWeight: activeTab === tab.id ? 600 : 400,
                  cursor: 'pointer',
                  whiteSpace: 'nowrap',
                }}
              >
                {tab.label}
              </button>
            ))}
          </div>

          {/* Tab Contents */}
          <div style={{ flex: 1, overflowY: 'auto', padding: '20px' }}>
            {/* 1. Overview Tab */}
            {activeTab === 'overview' && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                {task?.description && (
                  <div>
                    <h4 style={{ fontSize: '13px', fontWeight: 600, color: 'var(--text-secondary)', margin: '0 0 6px 0' }}>
                      DESCRIPTION
                    </h4>
                    <p style={{ fontSize: '14px', color: 'var(--text-primary)', margin: 0, whiteSpace: 'pre-wrap', lineHeight: 1.5 }}>
                      {task.description}
                    </p>
                  </div>
                )}

                {/* Dates & Duration Summary */}
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', background: 'var(--bg-surface)', padding: '14px', borderRadius: '10px' }}>
                  <div>
                    <span style={{ fontSize: '12px', color: 'var(--text-secondary)' }}>Due Date</span>
                    <div style={{ fontSize: '13px', fontWeight: 500, color: 'var(--text-primary)', marginTop: '2px' }}>
                      {task?.end_at ? new Date(task.end_at).toLocaleString() : 'No deadline'}
                    </div>
                  </div>
                  <div>
                    <span style={{ fontSize: '12px', color: 'var(--text-secondary)' }}>Estimated / Actual</span>
                    <div style={{ fontSize: '13px', fontWeight: 500, color: 'var(--text-primary)', marginTop: '2px' }}>
                      {task?.estimated_minutes ? `${task.estimated_minutes}m` : '—'} / {task?.actual_minutes || 0}m spent
                    </div>
                  </div>
                </div>

                {/* Tags */}
                {task?.tags && task.tags.length > 0 && (
                  <div>
                    <h4 style={{ fontSize: '13px', fontWeight: 600, color: 'var(--text-secondary)', margin: '0 0 6px 0' }}>
                      TAGS
                    </h4>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                      {task.tags.map((t) => (
                        <span key={t} style={{ background: 'rgba(59, 130, 246, 0.15)', color: 'var(--accent-primary)', padding: '2px 8px', borderRadius: '12px', fontSize: '12px' }}>
                          #{t}
                        </span>
                      ))}
                    </div>
                  </div>
                )}

                {/* Related Resource */}
                {task?.related_resource_type && (
                  <div style={{ padding: '12px', borderRadius: '8px', background: 'rgba(59, 130, 246, 0.1)', border: '1px solid rgba(59, 130, 246, 0.2)' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '13px', color: 'var(--accent-primary)' }}>
                      <Link2 size={16} />
                      <span>Linked {task.related_resource_type}: {task.related_resource_id}</span>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* 2. Subtasks Tab */}
            {activeTab === 'subtasks' && (
              <div>
                <form onSubmit={handleAddSubtask} style={{ display: 'flex', gap: '8px', marginBottom: '16px' }}>
                  <input
                    type="text"
                    className="input-field"
                    placeholder="Add a new subtask..."
                    value={newSubtaskTitle}
                    onChange={(e) => setNewSubtaskTitle(e.target.value)}
                    style={{ flex: 1 }}
                  />
                  <button type="submit" className="btn-primary" disabled={addingSubtask || !newSubtaskTitle.trim()}>
                    <Plus size={16} />
                  </button>
                </form>

                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                  {subtasks.length === 0 ? (
                    <div style={{ textAlign: 'center', padding: '24px', color: 'var(--text-secondary)', fontSize: '13px' }}>
                      No subtasks added yet. Break down this task into smaller steps.
                    </div>
                  ) : (
                    subtasks.map((st) => (
                      <div
                        key={st.id}
                        onClick={() => handleToggleSubtask(st)}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '10px',
                          padding: '10px 12px',
                          borderRadius: '8px',
                          background: 'var(--bg-surface)',
                          cursor: 'pointer',
                          transition: 'background 0.2s',
                        }}
                      >
                        {st.status === 'COMPLETED' ? (
                          <CheckCircle2 size={18} color="var(--success)" />
                        ) : (
                          <Circle size={18} color="var(--text-secondary)" />
                        )}
                        <span
                          style={{
                            fontSize: '13px',
                            color: st.status === 'COMPLETED' ? 'var(--text-secondary)' : 'var(--text-primary)',
                            textDecoration: st.status === 'COMPLETED' ? 'line-through' : 'none',
                          }}
                        >
                          {st.title}
                        </span>
                      </div>
                    ))
                  )}
                </div>
              </div>
            )}

            {/* 3. Timer Tab */}
            {activeTab === 'timer' && (
              <div>
                {/* Live Timer Widget */}
                <div
                  style={{
                    padding: '24px',
                    borderRadius: '12px',
                    background: 'linear-gradient(135deg, rgba(30, 41, 59, 0.8), rgba(15, 23, 42, 0.9))',
                    border: '1px solid var(--border-color)',
                    textAlign: 'center',
                    marginBottom: '20px',
                  }}
                >
                  <div style={{ fontSize: '36px', fontWeight: 700, fontFamily: 'monospace', color: timerRunning ? 'var(--accent-primary)' : 'var(--text-primary)', marginBottom: '12px' }}>
                    {formatSeconds(elapsedSeconds)}
                  </div>

                  <input
                    type="text"
                    className="input-field"
                    placeholder="Work session notes (optional)..."
                    value={timerNotes}
                    onChange={(e) => setTimerNotes(e.target.value)}
                    style={{ marginBottom: '16px', maxWidth: '360px' }}
                  />

                  <div>
                    {!timerRunning ? (
                      <button
                        onClick={handleStartTimer}
                        className="btn-primary"
                        style={{ display: 'inline-flex', alignItems: 'center', gap: '8px', minWidth: '140px' }}
                      >
                        <Play size={16} fill="currentColor" />
                        <span>Start Timer</span>
                      </button>
                    ) : (
                      <button
                        onClick={handleStopTimer}
                        className="btn-primary"
                        style={{ display: 'inline-flex', alignItems: 'center', gap: '8px', minWidth: '140px', background: 'var(--danger)' }}
                      >
                        <Square size={16} fill="currentColor" />
                        <span>Stop & Save</span>
                      </button>
                    )}
                  </div>
                </div>

                {/* Past Time Entries Log */}
                <h4 style={{ fontSize: '13px', fontWeight: 600, color: 'var(--text-secondary)', marginBottom: '10px' }}>
                  TIME ENTRIES LOG
                </h4>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                  {!task?.time_entries || task.time_entries.length === 0 ? (
                    <div style={{ fontSize: '13px', color: 'var(--text-secondary)', padding: '12px', textAlign: 'center' }}>
                      No time entries logged yet.
                    </div>
                  ) : (
                    task.time_entries.map((entry) => (
                      <div
                        key={entry.id}
                        style={{
                          padding: '10px 14px',
                          borderRadius: '8px',
                          background: 'var(--bg-surface)',
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                        }}
                      >
                        <div>
                          <div style={{ fontSize: '13px', color: 'var(--text-primary)', fontWeight: 500 }}>
                            {entry.notes || 'Timer Session'}
                          </div>
                          <div style={{ fontSize: '11px', color: 'var(--text-secondary)' }}>
                            {new Date(entry.started_at).toLocaleString()} • {entry.user_email}
                          </div>
                        </div>
                        <div style={{ fontSize: '14px', fontWeight: 600, color: 'var(--accent-primary)' }}>
                          {Math.round(entry.duration_seconds / 60)}m ({formatSeconds(entry.duration_seconds)})
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </div>
            )}

            {/* 4. Dependencies Tab */}
            {activeTab === 'dependencies' && (
              <div>
                {depError && (
                  <div style={{ padding: '10px 14px', background: 'rgba(239, 68, 68, 0.15)', color: 'var(--danger)', borderRadius: '8px', fontSize: '13px', marginBottom: '12px' }}>
                    {depError}
                  </div>
                )}

                <form onSubmit={handleAddDependency} style={{ display: 'flex', flexDirection: 'column', gap: '10px', marginBottom: '16px' }}>
                  <label style={{ fontSize: '12px', fontWeight: 500 }}>Add Dependency (This task depends on):</label>
                  <div style={{ display: 'flex', gap: '8px' }}>
                    <select
                      className="input-field"
                      value={selectedDepTaskId}
                      onChange={(e) => setSelectedDepTaskId(e.target.value)}
                      style={{ flex: 2 }}
                    >
                      <option value="">Select Predecessor Task...</option>
                      {allProjectTasks
                        .filter((t) => t.id !== taskId)
                        .map((t) => (
                          <option key={t.id} value={t.id}>
                            {t.title} ({t.status})
                          </option>
                        ))}
                    </select>

                    <select
                      className="input-field"
                      value={depType}
                      onChange={(e) => setDepType(e.target.value)}
                      style={{ flex: 1 }}
                    >
                      <option value="FINISH_TO_START">Finish to Start</option>
                      <option value="START_TO_START">Start to Start</option>
                      <option value="FINISH_TO_FINISH">Finish to Finish</option>
                      <option value="START_TO_FINISH">Start to Finish</option>
                    </select>

                    <button type="submit" className="btn-primary" disabled={!selectedDepTaskId}>
                      <Plus size={16} />
                    </button>
                  </div>
                </form>

                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                  {!task?.dependencies || task.dependencies.length === 0 ? (
                    <div style={{ fontSize: '13px', color: 'var(--text-secondary)', padding: '16px', textAlign: 'center' }}>
                      No dependencies set for this task.
                    </div>
                  ) : (
                    task.dependencies.map((dep) => (
                      <div
                        key={dep.id}
                        style={{
                          padding: '10px 14px',
                          borderRadius: '8px',
                          background: 'var(--bg-surface)',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                        }}
                      >
                        <div>
                          <div style={{ fontSize: '13px', color: 'var(--text-primary)', fontWeight: 500 }}>
                            {dep.depends_on_title}
                          </div>
                          <div style={{ fontSize: '11px', color: 'var(--text-secondary)' }}>
                            {dep.dependency_type} • Status: {dep.depends_on_status}
                          </div>
                        </div>
                        <button
                          onClick={() => handleRemoveDependency(dep.id)}
                          style={{ background: 'transparent', border: 'none', color: 'var(--danger)', cursor: 'pointer', padding: '4px' }}
                        >
                          <Trash2 size={15} />
                        </button>
                      </div>
                    ))
                  )}
                </div>
              </div>
            )}

            {/* 5. Comments Tab */}
            {activeTab === 'comments' && (
              <div>
                <form onSubmit={handleAddComment} style={{ display: 'flex', gap: '8px', marginBottom: '16px' }}>
                  <input
                    type="text"
                    className="input-field"
                    placeholder="Write a comment... Use @ to mention teammates"
                    value={commentText}
                    onChange={(e) => setCommentText(e.target.value)}
                    style={{ flex: 1 }}
                  />
                  <button type="submit" className="btn-primary" disabled={addingComment || !commentText.trim()}>
                    <Send size={16} />
                  </button>
                </form>

                <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                  {!task?.comments || task.comments.length === 0 ? (
                    <div style={{ fontSize: '13px', color: 'var(--text-secondary)', padding: '16px', textAlign: 'center' }}>
                      No comments yet. Start a discussion with your team.
                    </div>
                  ) : (
                    task.comments.map((comment) => (
                      <div
                        key={comment.id}
                        style={{
                          padding: '12px 14px',
                          borderRadius: '8px',
                          background: 'var(--bg-surface)',
                        }}
                      >
                        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px' }}>
                          <span style={{ fontSize: '12px', fontWeight: 600, color: 'var(--accent-primary)' }}>
                            {comment.user_email}
                          </span>
                          <span style={{ fontSize: '11px', color: 'var(--text-secondary)' }}>
                            {new Date(comment.created_at).toLocaleString()}
                          </span>
                        </div>
                        <div style={{ fontSize: '13px', color: 'var(--text-primary)', whiteSpace: 'pre-wrap' }}>
                          {comment.content}
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}
